import { ErrorInfo } from '../../utils/error-codes';
import { ErrorFields, ExchangeRates, ExportType, Pagination } from '../interfaces';

export interface Banks {
  [key: string]: string;
}

export enum FlowType {
  B2C = 'B2C',
  B2B = 'B2B',
}

export enum PayoutCreationStep {
  NONE = 0,
  ACCOUNT_DATA = 1,
  PAYOUT_DATA = 2,
  REVIEW = 3,
}

export enum BankAccountType {
  CHECKING = 'CHECKING',
  SAVINGS = 'SAVINGS',
}

export enum PurposeOfPayout {
  FAMILY_MAINTENANCE = 'FAMILY_MAINTENANCE',
  MEDICAL_CARE = 'MEDICAL_CARE',
  TUITION_COSTS = 'TUITION_COSTS',
  CHARITY_DONATION = 'CHARITY_DONATION',
  PROPERTY_PURCHASE = 'PROPERTY_PURCHASE',
  SHARES = 'SHARES',
  INVESTMENT = 'INVESTMENT',
  UTILITY_PROVIDER = 'UTILITY_PROVIDER',
  TAXES = 'TAXES',
  TOURISM_EXPENSE = 'TOURISM_EXPENSE',
  TRAVEL = 'TRAVEL',
  SAVINGS_RETIREMENT = 'SAVINGS_RETIREMENT',
  PENSION = 'PENSION',
  PAYROLL = 'PAYROLL',
  PHYSICAL_GOODS = 'PHYSICAL_GOODS',
  DIGITAL_GOODS = 'DIGITAL_GOODS',
  SUPPLIER = 'SUPPLIER',
  OTHER_SERVICES = 'OTHER_SERVICES',
  CAPITAL_CONTRIBUTION = 'CAPITAL_CONTRIBUTION',
  PROPERTY_RENTAL = 'PROPERTY_RENTAL',
  LOGISTICS = 'LOGISTICS',
  INVESTMENT_INTEREST = 'INVESTMENT_INTEREST',
  AVAILABILITY = 'AVAILABILITY',
  INSURANCE = 'INSURANCE',
  REWARDS = 'REWARDS',
  ENTERTAINMENT = 'ENTERTAINMENT',
  PUBLIC_SERVICES = 'PUBLIC_SERVICES',
  AFFILIATION_PROGRAMS = 'AFFILIATION_PROGRAMS',
  REMITTANCES = 'REMITTANCES',
}

export interface CreatePayoutData {
  transferAmount?: number;
  transferCountry?: string;
  flowType?: FlowType;
  purpose?: PurposeOfPayout;
  beneficiaryFirstName?: string;
  beneficiaryLastName?: string;
  businessName?: string;
  beneficiaryDocument?: string;
  beneficiaryDocumentType?: string;
  beneficiaryAddressStreet?: string;
  beneficiaryAddressCity?: string;
  bankCode?: string;
  bankAccount?: string;
  bankBranch?: string;
  bankAccountType?: string;
  currencyToPay?: string;
  description?: string;
  notificationUrl?: string;
}

export interface RequiredField {
  fieldPath: string;
  required: boolean;
  dataType: 'STRING' | 'NUMBER' | 'EMAIL' | 'SELECT' | 'DATE';
  maxLength?: number;
  description?: string;
  displayOrder: number;
}

export interface RequiredFieldsResponse {
  country: string;
  flowType: FlowType;
  version: number;
  baseRequiredFields: RequiredField[];
  additionalRequiredFields: RequiredField[];
}

export interface PayoutFee {
  feeAmount: number;
  feeCurrency: string;
  exchangeRate: string;
  youPayAmount?: number;
  totalDebitAmount?: number;
}

export interface PayoutEnabledCountryCurrency {
  currency: string;
  b2bEnabled: boolean;
}

export interface PayoutEnabledCountryConfiguration {
  country: string;
  currencies: PayoutEnabledCountryCurrency[];
}

export interface Filters {
  [filter: string]: string;
}

export interface Payout {
  amount: number;
  account_type: string;
  balance_fee_amount: number;
  balance_total_amount: number;
  bank_account: string;
  beneficiary_document: string;
  beneficiary_email: string;
  beneficiary_first_name: string;
  beneficiary_last_name: string;
  created_at: string;
  completed_at: string;
  currency_to_pay: string;
  description: string;
  exchange_rate: string;
  fee_amount: number;
  fee_currency: string;
  flow_type: string;
  payout_id: string;
  purpose: string;
  status: string;
  country: string;
  bank_name: string;
}

export interface PayoutsState {
  loading: boolean;
  error: boolean;
  allPayouts?: {
    data: Payout[];
  } & Pagination;
  requestPayout: {
    loading: boolean;
    error: boolean;
    errorInfo?: ErrorInfo;
    errorFields?: ErrorFields;
    banks: Banks;
    success: boolean;
  };
  payoutDetail: {
    loading: boolean;
    error: boolean;
    errorInfo?: ErrorInfo;
    data?: Payout;
  };
  export: {
    loading: boolean;
    error: boolean;
    errorInfo?: ErrorInfo;
    show: boolean;
    data?: any;
  };
  exportType?: ExportType;
  createPayout: {
    step: PayoutCreationStep;
    accountSubView: 'country' | 'bank-data';
    createPayoutData: CreatePayoutData;
    bankOptions: Array<{ code: string; name: string }>;
    requiredFields: {
      loading: boolean;
      error: boolean;
      response?: RequiredFieldsResponse;
      fields: RequiredField[];
    };
    fee: {
      loading: boolean;
      error: boolean;
      data?: PayoutFee;
    };
    partialValidation: {
      loading: boolean;
      error: boolean;
      success: boolean;
      errorInfo?: ErrorInfo;
      errorFields?: ErrorFields;
    };
    confirm: {
      loading: boolean;
      error: boolean;
      success: boolean;
      errorInfo?: ErrorInfo;
    };
    verifyOtpConfigured: {
      loading: boolean;
      error: boolean;
      success: boolean;
      errorInfo?: ErrorInfo;
      otpConfigured: boolean;
    };
    enabledCountryConfigurations: {
      loading: boolean;
      error: boolean;
      data: PayoutEnabledCountryConfiguration[];
    };
    currencies: {
      loading: boolean;
      error: boolean;
      exchangeRates: ExchangeRates;
    };
  };
}
